import { Component, ViewEncapsulation } from '@angular/core';
import { Router } from '@angular/router';
import {
    Color,
    getDynamicRouteLink,
    LifecycleAware,
    Modal,
    StatusColor,
    TableAppearance,
    Utilities
} from '@lsq-frontend/common-ui';
import {
    ActionSheetService,
    getRouterLinkUrlById,
    Icons,
    ResponsiveService
} from '@lsq-frontend/common-ui';
import { ModalService } from '@lsq-frontend/common-ui';
import { LsqExpandableDataRowComponent } from '@lsq-frontend/common-ui';
import { PopoverPlacement } from 'ngx-smart-popover';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { EnumDisplay } from '../../../constants/enum-display.constants';

import {
    atlas,
    CUSTOMER_COLLECTIONS,
    INVOICES_INVOICE
} from '@lsq-frontend/fasttrack-shared/routing-atlas';
import { GlobalSearchService } from '../../global-search/global-search.service';
import { ImagePreviewModalComponent } from '../../image-preview/image-preview-modal.component';
import {
    CollectionCheck,
    CollectionInvoice,
    GetCollectionInvoicesRequest,
    GetCollectionInvoicesService,
    ImageViewableStatus,
    PurchaseStatus
} from '@lsq-frontend/remote-services/enterprise/dashboard-services';
import { ApiDefaults, LsqListSource } from '@lsq-frontend/angular-shared/api-service';
import {
    RouteParams,
    FastTrackRouteQueryParams
} from '@lsq-frontend/fasttrack-shared/app-constants';

@LifecycleAware()
@Component({
    selector: 'collections-expandable-row',
    template: `
        <div *ngIf="!isSmallDevice" class="collections-expandable-group">
            <div class="expandable-row" *ngIf="dataSourceRow">
                <lsq-data-table
                    [dataSource]="dataSourceRow"
                    [appearance]="tableAppearance.ExpandableRow"
                    [layoutFixed]="true"
                    (click)="handleRowClick($event)"
                >
                    <lsq-column [column]="{ key: 'rowActions', width: '10%' }">
                        <ng-template [custom-cell] let-row="row">
                            <div
                                class="expandable-row-actions-cell"
                                (click)="handleActionCellClick($event)"
                            >
                                <div class="actions">
                                    <div
                                        class="expandable-row-checkbox"
                                        *ngIf="
                                            !(globalSearchService.checkNumberSearch$ | async) &&
                                            isCheckNumberClickable(row)
                                        "
                                    >
                                        <lsq-checkbox
                                            [id]="row.checkKey + '-checkbox'"
                                            [ngModel]="isChecked"
                                            (ngModelChange)="handleCheck($event)"
                                        >
                                        </lsq-checkbox>
                                    </div>
                                    <div
                                        *ngIf="rowIsExpandable"
                                        class="expandable-row-toggle"
                                        [ngClass]="{
                                            'is-full-width':
                                                (globalSearchService.checkNumberSearch$ | async) ||
                                                !isCheckNumberClickable(row)
                                        }"
                                        (click)="handleShowHideClick($event)"
                                    >
                                        <lsq-icon
                                            [type]="
                                                isExpanded ? icons.ChevronDown : icons.ChevronRight
                                            "
                                            color="black"
                                        ></lsq-icon>
                                        <span
                                            *ngIf="isExpanded"
                                            class="small-uppercase"
                                            i18n="Tells the user what to click to hide content"
                                        >
                                            Hide
                                        </span>
                                        <span
                                            *ngIf="!isExpanded"
                                            class="small-uppercase"
                                            i18n="Tells the user what to click to show content"
                                        >
                                            Show
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </ng-template>
                    </lsq-column>

                    <lsq-column [column]="{ key: 'debtorName', width: '34%' }">
                        <ng-template [custom-cell] let-row="row">
                            <div class="check-link-column">
                                <a
                                    [routerLink]="[
                                        getCustomerLink(collectionsRouterLink, row.debtorKey)
                                    ]"
                                    [queryParams]="getCustomerLinkQueryParams()"
                                    class="debtor-name preserve-case"
                                    [ngStyle]="{ 'font-weight': 'bold' }"
                                >
                                    <lsq-smart-truncate
                                        [value]="row.debtorName"
                                    ></lsq-smart-truncate>
                                </a>
                                <ng-container *ngIf="!!row.checkNumber">
                                    <a
                                        *ngIf="isCheckNumberClickable(row)"
                                        class="check-number"
                                        href="javascript:void(0)"
                                        (click)="handleCheckNumberClick($event, row)"
                                    >
                                        <b [innerHTML]="'CHECK #' + (checkNumberSpan$ | async)"></b>
                                    </a>

                                    <!-- Chargeback -->
                                    <span
                                        *ngIf="!isCheckNumberClickable(row)"
                                        class="chargeback-label"
                                    >
                                        <b [innerHTML]="'CHECK #' + (checkNumberSpan$ | async)"></b>
                                    </span>
                                </ng-container>
                            </div>
                        </ng-template>
                    </lsq-column>

                    <lsq-currency-column
                        [column]="{
                            key: 'checkInvoiceAmount',
                            width: '8%',
                            sortable: false
                        }"
                    >
                    </lsq-currency-column>

                    <lsq-currency-column
                        [column]="{
                            key: 'checkPaymentAmount',
                            width: '8%',
                            sortable: false
                        }"
                    >
                    </lsq-currency-column>

                    <lsq-currency-column
                        [column]="{
                            key: 'checkChargebacks',
                            width: '8%',
                            sortable: false
                        }"
                    >
                    </lsq-currency-column>

                    <lsq-currency-column
                        [column]="{
                            key: 'checkNonFactored',
                            width: '8%',
                            sortable: false
                        }"
                    >
                    </lsq-currency-column>

                    <lsq-currency-column
                        [column]="{
                            key: 'checkPreviousPayments',
                            width: '8%',
                            sortable: false
                        }"
                    >
                    </lsq-currency-column>

                    <lsq-currency-column
                        [column]="{
                            key: 'checkInvoiceBalance',
                            width: '8%',
                            sortable: false
                        }"
                    >
                    </lsq-currency-column>

                    <lsq-currency-column
                        [column]="{
                            key: 'checkCollectionFees',
                            width: '8%',
                            sortable: false
                        }"
                    >
                    </lsq-currency-column>
                </lsq-data-table>
            </div>

            <lsq-circle-loader
                *ngIf="isExpanded && innerTableIsLoading"
                class="expanded-row-loader"
            >
            </lsq-circle-loader>

            <div class="expanded-table" *ngIf="isExpanded && dataSourceInner && rowIsExpandable">
                <lsq-data-table
                    [dataSource]="dataSourceInner"
                    [isPaginated]="false"
                    [hasHeader]="false"
                    [hasSpinner]="false"
                    [layoutFixed]="true"
                >
                    <lsq-hidden-column width="10%"></lsq-hidden-column>

                    <lsq-hidden-column [column]="{ key: 'hidden2' }" width="3%">
                    </lsq-hidden-column>

                    <lsq-row-count-column width="3%"></lsq-row-count-column>

                    <lsq-column
                        [column]="{
                            header: 'Invoice #',
                            className: 'e2e-invoice-number-col',
                            key: 'invoiceNumber',
                            truncateTextCss: true,
                            width: '12%'
                        }"
                    >
                        <ng-template [custom-cell] let-row="row">
                            <div class="first-child-pl text-truncate">
                                <a
                                    [routerLink]="['/invoices', row.invoiceKey]"
                                    [ngStyle]="{ 'font-weight': 'bold' }"
                                >
                                    <lsq-smart-truncate
                                        [value]="row.invoiceNumber"
                                    ></lsq-smart-truncate>
                                </a>
                            </div>
                        </ng-template>
                    </lsq-column>

                    <lsq-date-column
                        [column]="{
                            header: 'Invoice Date',
                            className: 'invoice-date-col',
                            key: 'invoiceDate',
                            width: '8%'
                        }"
                    >
                    </lsq-date-column>

                    <lsq-date-column
                        [isDays]="true"
                        [column]="{
                            header: 'Invoice Age',
                            className: 'invoice-date-col',
                            key: 'invoiceAge',
                            width: '8%'
                        }"
                    >
                    </lsq-date-column>

                    <lsq-currency-column
                        [column]="{
                            key: 'invoiceAmount',
                            width: '8%',
                            sortable: false
                        }"
                    >
                    </lsq-currency-column>

                    <lsq-currency-column
                        [column]="{
                            key: 'paymentAmount',
                            width: '8%',
                            sortable: false
                        }"
                    >
                    </lsq-currency-column>

                    <lsq-currency-column
                        [column]="{
                            key: 'chargebacks',
                            width: '8%',
                            sortable: false
                        }"
                    >
                    </lsq-currency-column>

                    <lsq-currency-column
                        [column]="{
                            key: 'nonFactored',
                            width: '8%',
                            sortable: false
                        }"
                    >
                    </lsq-currency-column>

                    <lsq-currency-column
                        [column]="{
                            key: 'previousPayments',
                            width: '8%',
                            sortable: false
                        }"
                    >
                    </lsq-currency-column>

                    <lsq-currency-column
                        [column]="{
                            key: 'invoiceBalance',
                            width: '8%',
                            sortable: false
                        }"
                    >
                    </lsq-currency-column>

                    <lsq-currency-column
                        [column]="{
                            key: 'collectionFee',
                            width: '8%',
                            sortable: false
                        }"
                    >
                    </lsq-currency-column>
                </lsq-data-table>
            </div>
        </div>

        <ng-template #actionSheetTemplate>
            <ng-container *ngIf="mobileDrawerRows">
                <div class="invoice-cards" iosNestedScroll>
                    <div
                        *ngFor="let invoice of mobileDrawerRows"
                        class="invoice-card mtc-header"
                        (click)="goToInvoice(invoice)"
                    >
                        <h2 class="mtc-header-title-only font-weight-bold">
                            Invoice {{ invoice.invoiceNumber }}
                        </h2>
                        <a class="mtc-header-chevron">
                            <lsq-icon [color]="color.Gray" [type]="icons.ChevronRight"> </lsq-icon>
                        </a>
                    </div>
                </div>
            </ng-container>
        </ng-template>
    `,
    styleUrls: ['./collections.expandable-row.component.scss'],
    encapsulation: ViewEncapsulation.None
})
export class CollectionsExpandableRowComponent extends LsqExpandableDataRowComponent<
    CollectionCheck,
    CollectionInvoice
> {
    // Constants.
    public color = Color;
    public enumDisplay = EnumDisplay;
    public icons = Icons;
    public placement = PopoverPlacement;
    public purchaseStatus = PurchaseStatus;
    public routeQueryParams = FastTrackRouteQueryParams;
    public statusColor = StatusColor;
    public tableAppearance = TableAppearance;
    public utilities = Utilities;
    public collectionsRouterLink = getRouterLinkUrlById(atlas, CUSTOMER_COLLECTIONS);
    public checkNumberSpan$: Observable<string>;

    constructor(
        private _getCollectionInvoicesService: GetCollectionInvoicesService,
        public globalSearchService: GlobalSearchService,
        public router: Router,
        public responsiveService: ResponsiveService,
        public actionSheetService: ActionSheetService,
        public modalService: ModalService
    ) {
        super(responsiveService, actionSheetService);

        this.checkNumberSpan$ = this.globalSearchService.checkNumberSearch$.pipe(
            map((checkNumberSearch) =>
                checkNumberSearch
                    ? this.highlightSearch(this.row.checkNumber, checkNumberSearch)
                    : this.row.checkNumber
            )
        );
    }

    public async handleInitialExpand(): Promise<void> {
        const response = await this._getCollectionInvoicesService.executeOnce(
            new GetCollectionInvoicesRequest({
                debtorKey: this.row.debtorKey,
                checkKey: parseInt(this.row.checkKey.substring(this.row.checkKey.indexOf('-') + 1)),
                transKey: this.row.transKey,
                take: ApiDefaults.TakeAll
            })
        );
        this.dataSourceInner = new LsqListSource<CollectionInvoice>({
            data$: of(response),
            rows$: of(response.rows)
        });

        // Set up context for mobile action sheet.
        this.mobileDrawerRows = response ? response.rows : [];
    }

    public getCustomerLink(link: string, debtorKey: string): string {
        return getDynamicRouteLink(link, { debtorKey });
    }

    public getCustomerLinkQueryParams(): object {
        if (!this.fromDateFilter || !this.toDateFilter) {
            return {};
        }

        return {
            [FastTrackRouteQueryParams.StartDate]: Utilities.getISOStringWithoutTimezone(
                new Date(this.fromDateFilter)
            ),
            [FastTrackRouteQueryParams.EndDate]: Utilities.getISOStringWithoutTimezone(
                new Date(this.toDateFilter)
            )
        };
    }

    public isCheckNumberClickable(check: CollectionCheck): boolean {
        return (
            check.imageViewableStatus !== ImageViewableStatus.NotViewable &&
            !!check.checkKey &&
            check.checkAmount > 0
        );
    }

    public async handleCheckNumberClick(event: MouseEvent, check: CollectionCheck): Promise<void> {
        event.stopImmediatePropagation();

        if (check.imageViewableStatus === ImageViewableStatus.Viewable) {
            this.modalService.showModal(
                new Modal({
                    id: 'image-preview-modal',
                    title: `Check #${check.checkNumber}`,
                    component: ImagePreviewModalComponent,
                    width: 'auto',
                    data: {
                        title: `Check #${check.checkNumber}`,
                        checkKey: check.checkKey,
                        filename: `check_${check.checkNumber}`
                    }
                })
            );
        } else if (check.imageViewableStatus === ImageViewableStatus.Archived) {
            this.modalService.showModal(
                new Modal({
                    id: 'check-archived-modal',
                    title: `Check #${check.checkNumber}`,
                    bodyHtml:
                        '<p>This check image is not available to view. You can request a copy of this check from the <a href="mailto:accountmanagers@lsq.com">Client Services team.</a></p>',
                    width: 'auto',
                    showFooter: true,
                    showOkButton: true,
                    okButtonText: 'Close'
                })
            );
        }
    }

    public async goToInvoice(invoice: CollectionInvoice): Promise<void> {
        await this.router.navigateByUrl(
            getRouterLinkUrlById(atlas, INVOICES_INVOICE).replace(
                RouteParams.InvoiceKey,
                `${invoice.invoiceKey}`
            )
        );
        this.actionSheetService.closeActionSheet();
    }

    public highlightSearch(content: string, searchString: string): string {
        return content.replace(
            searchString,
            (match) => `<span class="highlight-search">${match}</span>`
        );
    }
}
